import React, {useCallback, useMemo, useState} from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Box, Card, Divider, Tooltip, Typography} from "@mui/material";
import {InfoOutlined} from "@mui/icons-material";
import Form, {MuiRFF, Yup} from "../../../../../../../components/form";
import ColorTextField from "../../../../../../../components/text-fields/color-field";
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import Button from "../../../../../../../components/base/button/button";
import {TinyColor} from "@ctrl/tinycolor";
import DefaultColorTheme from "../../../../../../../theme/default-color-theme";
import lodash from "lodash";
import usePartnerColorTheme from "../../../../../../../hooks/use-partner-color-theme";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";

export const PartnerColorThemeConfigurationsSection = () => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    const {partnerColorTheme, loading, setPartnerColorTheme} = usePartnerColorTheme({
        autoFetch: true, autoFetchArgs: {disregardCache: true}
    });

    const initialValues = useMemo(() => ({
        primary: partnerColorTheme.light?.palette?.primary?.main,
        secondary: partnerColorTheme.light?.palette?.secondary?.main,
        tertiary: partnerColorTheme.light?.palette?.tertiary?.main,
    }), [partnerColorTheme.light?.palette?.primary?.main, partnerColorTheme.light?.palette?.secondary?.main, partnerColorTheme.light?.palette?.tertiary?.main])

    const schema = useMemo(() => {
        return Yup.object().shape({
            primary: Yup.string().required(ValidateMessages.required),
            secondary: Yup.string().required(ValidateMessages.required),
            tertiary: Yup.string().required(ValidateMessages.required),
        });
    }, []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const getLight = React.useCallback((color, amount = 85) => new TinyColor(color).mix('#ffffff', amount).toHexString(), []);
    const getLighter = React.useCallback((color, amount = 93) => new TinyColor(color).mix('#ffffff', amount).toHexString(), []);
    const getDarkModeMain = React.useCallback((color) => new TinyColor(color).lighten(20).brighten(5).toHexString(), []);
    const getDarkModeTertiary = React.useCallback((color) => new TinyColor(color).spin(-137).saturate(17).brighten(50).toHexString(), []);

    /**
     * Generates a full color theme JSON object from three base colors.
     *
     * @param primary The primary color (hex)
     * @param secondary The secondary color (hex)
     * @param tertiary The tertiary color (hex)
     * @returns A full theme object matching the DefaultColorTheme structure.
     */
    const generateColorTheme = useCallback((primary, secondary, tertiary) => {
        const theme = lodash.cloneDeep(DefaultColorTheme);

        // Light Mode Palette
        theme.light.palette.primary.main = primary;
        theme.light.palette.primary.light = getLight(primary, 90);

        theme.light.palette.secondary.main = secondary;
        theme.light.palette.secondary.light = getLight(secondary, 85);
        theme.light.palette.secondary.lighter = getLighter(secondary, 93);

        theme.light.palette.tertiary.main = tertiary;
        theme.light.palette.tertiary.light = getLight(tertiary, 90);

        // Dark Mode Palette
        // For dark mode, we lighten the main colors to pop against the dark background
        theme.dark.palette.primary.main = getDarkModeMain(primary);
        theme.dark.palette.secondary.main = getDarkModeMain(secondary);
        theme.dark.palette.tertiary.main = getDarkModeTertiary(tertiary);

        return theme;
    }, [getDarkModeMain, getLight, getLighter, getDarkModeTertiary])

    /**
     * Updates the full color theme based on the 3 base colors (Mocked)
     */
    const onSubmit = useCallback(async (values) => {
        setSubmitting(true);
        const fullTheme = generateColorTheme(values.primary, values.secondary, values.tertiary);
        await setPartnerColorTheme(fullTheme);
        if (!isMounted())
            return;
        setSubmitting(false);
    }, [generateColorTheme, setPartnerColorTheme, isMounted]);

    if (loading) {
        return (
            <Grid2 xs={12}>
                <div className={'loading access-level w-100'}>
                    <div>
                        <div/>
                    </div>
                </div>
            </Grid2>
        );
    }

    return (
        <Grid2 xs={12}>
            <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                <Form
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: 'partner-color-theme-form'}}
                >
                    {({values, initialValues}) => {
                        return (
                            <>
                                <Grid2 container spacing={2}>
                                    <Grid2 xs={12}>
                                        <Typography variant={'subtitle1'} fontWeight={600}>
                                            Partner Color Theme
                                        </Typography>
                                    </Grid2>
                                    <Grid2 xs={12}>
                                        <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                                    </Grid2>

                                    <Grid2 xs={12} lg={4}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1}}>
                                            <Typography variant="body2" fontWeight={500}>Primary Color</Typography>
                                            <Tooltip title={'The main brand color (Light Mode). Dark mode variants will be generated automatically.'}>
                                                <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                            </Tooltip>
                                        </Box>
                                        <ColorTextField
                                            form
                                            required={required.primary}
                                            name="primary"
                                            getAlternateColor={getDarkModeMain}
                                            disabled={submitting}
                                        />
                                    </Grid2>

                                    <Grid2 xs={12} lg={4}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1}}>
                                            <Typography variant="body2" fontWeight={500}>Secondary Color</Typography>
                                            <Tooltip title={'The secondary brand color. Used for accents and less prominent elements.'}>
                                                <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                            </Tooltip>
                                        </Box>
                                        <ColorTextField
                                            form
                                            required={required.secondary}
                                            name="secondary"
                                            getAlternateColor={getDarkModeMain}
                                            disabled={submitting}
                                        />
                                    </Grid2>

                                    <Grid2 xs={12} lg={4}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1}}>
                                            <Typography variant="body2" fontWeight={500}>Tertiary Color</Typography>
                                            <Tooltip title={'The tertiary color. Used for subtle backgrounds or additional accents.'}>
                                                <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                            </Tooltip>
                                        </Box>
                                        <ColorTextField
                                            form
                                            required={required.tertiary}
                                            name="tertiary"
                                            getAlternateColor={getDarkModeTertiary}
                                            disabled={submitting}
                                        />
                                    </Grid2>

                                    <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'flex-end', mt: 3}}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={submitting || (initialValues.primary === values.primary && initialValues.secondary === values.secondary && initialValues.tertiary === values.tertiary)}
                                            form="partner-color-theme-form"
                                            sx={{minWidth: 200}}
                                        >
                                            {submitting ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Grid2>
                                </Grid2>
                            </>
                        )
                    }}
                </Form>
            </Card>
        </Grid2>
    );
};

